import type { DiagramNode } from '@civil/schema';
import type { ProjectBundle } from '../project.js';

/**
 * Between Flow and Architecture (prd-deltas §25). Both describe the whole application:
 * Flow what it does, Architecture what it is made of. A role in one is carried by
 * pieces of the other, and those pieces may sit anywhere in the folders. Nothing new
 * is stored to say so: a node's `realizedBy` names the composition parts behind it, and
 * its evidence names the files. Two nodes are counterparts when they share a part, or,
 * failing that, cite the same file.
 */

export interface Counterpart {
  view: string;
  diagram: string;
  diagramName: string;
  node: DiagramNode;
  /** What the two have in common, said so the link explains itself. */
  via: { parts: string[]; files: string[] };
}

/** "src/auth.ts:12" and "src/auth.ts" are the same file. */
const fileOf = (evidence: string) => evidence.replace(/:\d+(?:-\d+)?$/, '');

function evidenceFiles(node: DiagramNode): Set<string> {
  return new Set([
    ...node.evidence.map(fileOf),
    ...node.decisions.flatMap((d) => d.evidence.map(fileOf)),
  ]);
}

/** The other of the two views a link crosses to; a project's own views are not crossed. */
export const across = (view: string): string | undefined =>
  view === 'flow' ? 'architecture' : view === 'architecture' ? 'flow' : undefined;

/** Nodes of `toView` that carry the same parts or cite the same files as `node`, closest first. */
export function counterparts(bundle: ProjectBundle, node: DiagramNode, toView: string): Counterpart[] {
  const loaded = bundle.views?.[toView];
  if (!loaded?.diagrams) return [];
  const parts = new Set(node.realizedBy);
  const files = evidenceFiles(node);
  const found: Counterpart[] = [];
  for (const [diagram, { doc }] of Object.entries(loaded.diagrams)) {
    for (const other of doc.spec.nodes) {
      const sharedParts = other.realizedBy.filter((p) => parts.has(p));
      const sharedFiles = [...evidenceFiles(other)].filter((f) => files.has(f));
      if (sharedParts.length === 0 && sharedFiles.length === 0) continue;
      found.push({
        view: toView,
        diagram,
        diagramName: doc.metadata.name ?? diagram,
        node: other,
        via: { parts: sharedParts, files: sharedFiles },
      });
    }
  }
  // Sharing a part is the stronger tie; files break ties, then the name reads steady.
  return found.sort(
    (a, b) =>
      b.via.parts.length - a.via.parts.length ||
      b.via.files.length - a.via.files.length ||
      a.node.name.localeCompare(b.node.name),
  );
}

/** Nodes of a view that a composition part helps carry: a part seen from Flow or Architecture. */
export function carriedBy(bundle: ProjectBundle, part: string, view: string): Counterpart[] {
  const loaded = bundle.views?.[view];
  if (!loaded?.diagrams) return [];
  const found: Counterpart[] = [];
  for (const [diagram, { doc }] of Object.entries(loaded.diagrams)) {
    for (const node of doc.spec.nodes) {
      if (node.realizedBy.includes(part)) {
        found.push({ view, diagram, diagramName: doc.metadata.name ?? diagram, node, via: { parts: [part], files: [] } });
      }
    }
  }
  return found.sort((a, b) => a.node.name.localeCompare(b.node.name));
}

/**
 * Every file a node rests on: its evidence and its decisions', then the files of the
 * parts behind it as the repository read recorded them. However scattered across
 * folders, they are listed together, which is the point.
 */
export function filesBehind(bundle: ProjectBundle, node: DiagramNode): string[] {
  const units = bundle.lift?.units ?? {};
  const all = new Set(evidenceFiles(node));
  for (const part of node.realizedBy) for (const file of units[part]?.files ?? []) all.add(file);
  const known = new Set(bundle.files);
  return [...all].filter((file) => known.has(file)).sort();
}
